/**
 * Búsqueda externa de alimentos con valores nutricionales reales.
 *
 * Fuentes (en orden de prioridad):
 *  1. FatSecret Platform       — mayor base de datos, productos de marca, búsqueda por código de barras
 *  2. USDA FoodData Central    — ingredientes básicos, muy preciso
 *  3. Open Food Facts          — alimentos procesados españoles/europeos (sin clave)
 *
 * FatSecret y USDA pasan por el CF Worker proxy (clave nunca en el bundle).
 * Open Food Facts soporta CORS directo.
 */

import type { FoodCategory, FoodItem } from './DataService';

// ── USDA FoodData Central ────────────────────────────────────────────────────

const USDA_BASE = 'https://api.nal.usda.gov/fdc/v1';

// Nutrient IDs estándar de USDA
const N = {
  kcal:    1008,
  protein: 1003,
  fat:     1004,
  carbs:   1005,
  fiber:   1079,
} as const;

interface UsdaNutrient { nutrientId: number; value: number }
interface UsdaFood {
  fdcId: number;
  description: string;
  dataType: string;
  foodCategory?: string;
  servingSize?: number;
  servingSizeUnit?: string;
  foodNutrients: UsdaNutrient[];
}

function usdaCategory(cat?: string): FoodCategory {
  if (!cat) return 'otro';
  const c = cat.toLowerCase();
  if (c.includes('poultry') || c.includes('beef') || c.includes('pork') || c.includes('fish') || c.includes('seafood') || c.includes('lamb') || c.includes('veal')) return 'proteína';
  if (c.includes('dairy') || c.includes('cheese') || c.includes('milk') || c.includes('yogurt')) return 'lácteo';
  if (c.includes('vegetable') || c.includes('legume')) return 'vegetal';
  if (c.includes('fruit')) return 'fruta';
  if (c.includes('grain') || c.includes('cereal') || c.includes('bread') || c.includes('pasta') || c.includes('rice')) return 'cereal';
  if (c.includes('fat') || c.includes('oil') || c.includes('nut') || c.includes('seed')) return 'grasa';
  if (c.includes('beverage') || c.includes('drink')) return 'bebida';
  if (c.includes('snack')) return 'snack';
  return 'plato_combinado';
}

function mapUsdaFood(food: UsdaFood): FoodItem | null {
  const get = (id: number) => food.foodNutrients.find(n => n.nutrientId === id)?.value ?? 0;
  const kcal = get(N.kcal);
  if (!kcal) return null;

  // USDA descriptions are ALL CAPS — convert to Title Case
  const displayName = food.description
    .split(',')[0]
    .toLowerCase()
    .replace(/\b\w/g, c => c.toUpperCase())
    .trim();

  return {
    id: `usda_${food.fdcId}`,
    name: displayName.toLowerCase(),
    displayName: `${displayName} (USDA)`,
    aliases: [],
    category: usdaCategory(food.foodCategory),
    kcalPer100g:    Math.round(kcal),
    proteinPer100g: Math.round(get(N.protein) * 10) / 10,
    carbsPer100g:   Math.round(get(N.carbs)   * 10) / 10,
    fatPer100g:     Math.round(get(N.fat)      * 10) / 10,
    fiberPer100g:   Math.round(get(N.fiber)    * 10) / 10,
    typicalServingG: food.servingSize ?? 100,
    typicalServingLabel: food.servingSize ? `${food.servingSize}${food.servingSizeUnit ?? 'g'}` : '100g',
    source: 'usda',
    analysisCount: 0,
    verifiedByNutritionist: true,
  };
}

export async function searchUsda(query: string): Promise<FoodItem[]> {
  try {
    // Opción A (más segura): proxy Cloudflare Worker — la API key vive en Cloudflare Secrets
    // Opción B (fallback): llamada directa con DEMO_KEY o clave inyectada por CI
    const proxyUrl = import.meta.env.VITE_USDA_PROXY_URL as string | undefined;
    const key      = import.meta.env.VITE_USDA_API_KEY  as string | undefined;

    let url: string;
    if (proxyUrl) {
      url = `${proxyUrl}/foods/search?` + new URLSearchParams({ query });
    } else {
      url = `${USDA_BASE}/foods/search?` + new URLSearchParams({
        query,
        api_key:  key ?? 'DEMO_KEY',
        dataType: 'Foundation,Survey (FNDDS)',
        pageSize: '8',
      });
    }

    const res = await fetch(url, { signal: AbortSignal.timeout(5000) });
    if (!res.ok) return [];
    const data = await res.json() as { foods?: UsdaFood[] };
    return (data.foods ?? []).map(mapUsdaFood).filter((f): f is FoodItem => f !== null);
  } catch {
    return [];
  }
}

// ── Open Food Facts ──────────────────────────────────────────────────────────
// Soporta búsqueda multilingüe; muy útil para alimentos españoles procesados.

interface OffProduct {
  code?: string;
  product_name_es?: string;
  product_name?: string;
  nutriments?: {
    'energy-kcal_100g'?: number;
    energy_100g?: number;
    proteins_100g?: number;
    carbohydrates_100g?: number;
    fat_100g?: number;
    fiber_100g?: number;
  };
}

function mapOffProduct(p: OffProduct, query: string): FoodItem | null {
  const n = p.nutriments ?? {};
  const kcal = n['energy-kcal_100g'] ?? (n.energy_100g ? n.energy_100g / 4.184 : 0);
  if (!kcal || kcal < 1) return null;

  const name = p.product_name_es ?? p.product_name ?? query;
  return {
    id: `off_${p.code ?? Math.random()}`,
    name: name.toLowerCase(),
    displayName: `${name} (OFF)`,
    aliases: [],
    category: 'otro',
    kcalPer100g:    Math.round(kcal),
    proteinPer100g: Math.round((n.proteins_100g      ?? 0) * 10) / 10,
    carbsPer100g:   Math.round((n.carbohydrates_100g ?? 0) * 10) / 10,
    fatPer100g:     Math.round((n.fat_100g           ?? 0) * 10) / 10,
    fiberPer100g:   Math.round((n.fiber_100g         ?? 0) * 10) / 10,
    typicalServingG: 100,
    typicalServingLabel: '100g',
    source: 'openfoodfacts',
    analysisCount: 0,
    verifiedByNutritionist: false,
  };
}

export async function searchOpenFoodFacts(query: string): Promise<FoodItem[]> {
  try {
    const url = 'https://world.openfoodfacts.org/cgi/search.pl?' + new URLSearchParams({
      search_terms: query,
      json:         '1',
      action:       'process',
      fields:       'code,product_name,product_name_es,nutriments',
      page_size:    '8',
      lc:           'es',
    });
    // OFF cgi/search.pl es lento; 8s para que no se aborte antes de responder.
    const res = await fetch(url, { signal: AbortSignal.timeout(8000) });
    if (!res.ok) return [];
    const data = await res.json() as { products?: OffProduct[] };
    return (data.products ?? [])
      .map(p => mapOffProduct(p, query))
      .filter((f): f is FoodItem => f !== null);
  } catch {
    return [];
  }
}

// ── FatSecret Platform ───────────────────────────────────────────────────────
// Proxy a través del CF Worker (clave OAuth nunca en el bundle del cliente).

interface FatSecretFood {
  food_id: string;
  food_name: string;
  food_type: 'Generic' | 'Brand';
  brand_name?: string;
  // "Per 100g - Calories: 165kcal | Fat: 3.57g | Carbs: 0.00g | Protein: 31.02g"
  // o "Per 1 oz (28g) - Calories: 160kcal | ..."
  food_description: string;
}

interface FatSecretServing {
  serving_description: string;
  metric_serving_amount?: string;
  metric_serving_unit?: string;
  calories: string;
  carbohydrate: string;
  protein: string;
  fat: string;
  fiber?: string;
}

interface FatSecretFoodDetail {
  food_id: string;
  food_name: string;
  food_type: string;
  brand_name?: string;
  servings: { serving: FatSecretServing | FatSecretServing[] };
}

function parseFsDescription(desc: string): {
  kcalPer100g: number; prot: number; carbs: number; fat: number; servingG: number
} | null {
  // Extraer gramos de la porción de referencia
  const gMatch = desc.match(/Per\s+[\d.]+\s+\w+\s*\(([\d.]+)\s*g\)|Per\s+([\d.]+)\s*g/i);
  const servingG = gMatch ? parseFloat(gMatch[1] ?? gMatch[2]) : 100;

  const m = desc.match(
    /Calories:\s*([\d.]+)kcal.*?Fat:\s*([\d.]+)g.*?Carbs:\s*([\d.]+)g.*?Protein:\s*([\d.]+)g/i,
  );
  if (!m) return null;
  const f = servingG > 0 ? 100 / servingG : 1;
  return {
    kcalPer100g: Math.round(parseFloat(m[1]) * f),
    fat:         Math.round(parseFloat(m[2]) * f * 10) / 10,
    carbs:       Math.round(parseFloat(m[3]) * f * 10) / 10,
    prot:        Math.round(parseFloat(m[4]) * f * 10) / 10,
    servingG,
  };
}

function mapFatSecretFood(f: FatSecretFood): FoodItem | null {
  const macros = parseFsDescription(f.food_description);
  if (!macros || !macros.kcalPer100g) return null;
  const brand = f.brand_name ? ` (${f.brand_name})` : '';
  return {
    id: `fs_${f.food_id}`,
    name: f.food_name.toLowerCase(),
    displayName: `${f.food_name}${brand}`,
    aliases: [],
    category: 'otro',
    kcalPer100g:    macros.kcalPer100g,
    proteinPer100g: macros.prot,
    carbsPer100g:   macros.carbs,
    fatPer100g:     macros.fat,
    typicalServingG: macros.servingG,
    typicalServingLabel: `${macros.servingG}g`,
    source: 'fatsecret',
    analysisCount: 0,
    verifiedByNutritionist: false,
  };
}

function mapFatSecretDetail(food: FatSecretFoodDetail): FoodItem | null {
  const servArr = Array.isArray(food.servings.serving)
    ? food.servings.serving
    : [food.servings.serving];
  // Preferir porción métrica (g/ml) sobre "1 serving"
  const s = servArr.find(x => parseFloat(x.metric_serving_amount ?? '0') > 0) ?? servArr[0];
  if (!s) return null;
  const grams   = parseFloat(s.metric_serving_amount ?? '100');
  const f       = grams > 0 ? 100 / grams : 1;
  const kcal    = Math.round(parseFloat(s.calories) * f);
  if (!kcal) return null;
  const brand   = food.brand_name ? ` (${food.brand_name})` : '';
  return {
    id: `fs_${food.food_id}`,
    name: food.food_name.toLowerCase(),
    displayName: `${food.food_name}${brand}`,
    aliases: [],
    category: 'otro',
    kcalPer100g:    kcal,
    proteinPer100g: Math.round(parseFloat(s.protein)      * f * 10) / 10,
    carbsPer100g:   Math.round(parseFloat(s.carbohydrate) * f * 10) / 10,
    fatPer100g:     Math.round(parseFloat(s.fat)          * f * 10) / 10,
    fiberPer100g:   s.fiber ? Math.round(parseFloat(s.fiber) * f * 10) / 10 : undefined,
    typicalServingG: Math.round(grams),
    typicalServingLabel: s.serving_description,
    source: 'fatsecret',
    analysisCount: 0,
    verifiedByNutritionist: false,
  };
}

function proxyBase(): string | undefined {
  return import.meta.env.VITE_USDA_PROXY_URL as string | undefined;
}

export async function searchFatSecret(query: string): Promise<FoodItem[]> {
  const base = proxyBase();
  if (!base) return [];
  try {
    const url = `${base}/fatsecret/search?` + new URLSearchParams({ query });
    const res  = await fetch(url, { signal: AbortSignal.timeout(5000) });
    if (!res.ok) return [];
    const data = await res.json() as { foods?: { food?: FatSecretFood | FatSecretFood[] } };
    const raw  = data.foods?.food ?? [];
    const arr  = Array.isArray(raw) ? raw : [raw];
    return arr.map(mapFatSecretFood).filter((x): x is FoodItem => x !== null);
  } catch {
    return [];
  }
}

// Barcode lookup: FatSecret primero (mejor cobertura), fallback Open Food Facts
async function lookupBarcodeFatSecret(barcode: string): Promise<FoodItem | null> {
  const base = proxyBase();
  if (!base) return null;
  try {
    const url  = `${base}/fatsecret/barcode?` + new URLSearchParams({ code: barcode });
    const res  = await fetch(url, { signal: AbortSignal.timeout(7000) });
    if (!res.ok) return null;
    const data = await res.json() as { food?: FatSecretFoodDetail };
    return data.food ? mapFatSecretDetail(data.food) : null;
  } catch {
    return null;
  }
}

async function lookupBarcodeOFF(barcode: string): Promise<FoodItem | null> {
  try {
    const res  = await fetch(
      `https://world.openfoodfacts.org/api/v2/product/${barcode}.json?fields=product_name,product_name_es,nutriments`,
      { signal: AbortSignal.timeout(6000) },
    );
    if (!res.ok) return null;
    const data = await res.json() as {
      status: number;
      product?: OffProduct & { product_name?: string };
    };
    if (data.status !== 1 || !data.product) return null;
    return mapOffProduct({ ...data.product, code: barcode }, barcode);
  } catch {
    return null;
  }
}

export async function lookupBarcode(barcode: string): Promise<FoodItem | null> {
  const fs = await lookupBarcodeFatSecret(barcode);
  if (fs) return fs;
  return lookupBarcodeOFF(barcode);
}

// ── Búsqueda combinada ───────────────────────────────────────────────────────

export async function searchFoodsExternal(query: string): Promise<FoodItem[]> {
  if (!query || query.trim().length < 2) return [];

  // FatSecret + USDA + OFF en paralelo
  const [fs, usda, off] = await Promise.all([
    searchFatSecret(query),
    searchUsda(query),
    searchOpenFoodFacts(query),
  ]);

  // Intercalado round-robin de las 3 fuentes: garantiza diversidad para que OFF y
  // FatSecret aparezcan aunque USDA devuelva muchos resultados (antes USDA copaba
  // el tope de 12 y desplazaba a las otras fuentes). Orden de preferencia por ronda:
  // FatSecret → USDA → OFF.
  const sources = [fs, usda, off];
  const seen    = new Set<string>();
  const results: FoodItem[] = [];
  const maxLen  = Math.max(fs.length, usda.length, off.length);

  for (let i = 0; i < maxLen && results.length < 12; i++) {
    for (const src of sources) {
      if (results.length >= 12) break;
      const item = src[i];
      if (!item) continue;
      const key = item.name.slice(0, 30);
      if (!seen.has(key)) {
        seen.add(key);
        results.push(item);
      }
    }
  }

  return results;
}
